import React from "react";
import styled from "styled-components";

import {
    CardContent as MuiCardContent,
    Chip as MuiChip,
    Grid,
    ButtonBase,
    Tooltip,
} from "@material-ui/core";
import {
    Card,
    FlexRowSpaceBetween,
    Typography,
} from "../../../../../common/components/libs";
import { Period, Title } from "../../../../../services/stats/models";
import {Trans, useTranslation} from "react-i18next";

const red = {
    color: "rgb(244, 67, 54)",
    background: "rgba(244, 67, 54, 0.1)"
}

const green = {
    color: "rgb(76, 175, 80)",
    background: "rgba(76, 175, 80, 0.1)"
}


const Chip = styled(MuiChip)`
  position: absolute;
  top: 16px;
  right: 16px;
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  font-weight: 900;
  background-color: ${(props) => props.theme.palette.secondary.main};
  color: ${(props) => props.theme.palette.common.white};
  margin-bottom: ${(props) => props.theme.spacing(4)}px;

  span {
    padding-left: ${(props) => props.theme.spacing(2)}px;
    padding-right: ${(props) => props.theme.spacing(2)}px;
    font-size: 14px;
  }
`;

const CardContent = styled(MuiCardContent)`
  position: relative;
  cursor: pointer;

  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(4)}px;
  }
`;


type StatsPropsType = {
    title: string;
    amount: string;
    chip?: string;
    percentageText?: string;
    percentagecolor?: string;
    showGraphic?: () => void;
    prevAmount?: string;
    periodName?: Period;
};

const Stats = (props: StatsPropsType) => {
    const {i18n, t} = useTranslation(["general", "stats"]);
    const locale = i18n.language;
    let {
        title,
        amount,
        showGraphic,
        prevAmount,
        periodName
    } = props;

    let colorSettings = parseFloat(prevAmount) < 0 ? red : green;
    const makingLabel = () => {
        let status = t("stats:more");
        let sign = "+";
        if (parseFloat(prevAmount) < 0) {
            sign = "-";
            status = t("stats:less");
            prevAmount = (parseFloat(prevAmount) * -1).toFixed(1);
        }
        let titleStat = title;
        if (title === Title.ordersAmount) {
            titleStat = t("stats:orders");
        }
        switch (periodName) {
            case Period.today: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromYesterday" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromYesterday")
                };
            }

            case Period.yesterday: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromDayBeforeYesterday" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromDayBeforeYesterday")
                };
            }

            case Period.beginning_week: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromLastWeek" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label:  t("stats:fromLastWeek")
                };
            }

            case Period.previous_week: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromWeekBeforeLastWeek" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromWeekBeforeLastWeek")
                };
            }

            case Period.beginning_month: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromLastMonth" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label:  t("stats:fromLastMonth")
                };
            }

            case Period.previous_month: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromMonthBeforeLastMonth" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromMonthBeforeLastMonth")
                };
            }

            case Period.beginning_quarter: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromLastQuarter" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromLastQuarter")
                };
            }

            case Period.previous_quarter: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromQuarterBeforeLastQuarter" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromQuarterBeforeLastQuarter")
                };
            }

            case Period.beginning_year: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromLastYear" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromLastYear")
                };
            }

            case Period.previous_year: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromYearBeforeLastYear" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromYearBeforeLastYear")
                };
            }

            case Period.custom_period: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromPeriodBeforeLastPeriod" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromLastPeriod")
                };
            }

            default: {
                return {
                    percentage: `${sign}${prevAmount}%`,
                    tooltip: <Trans i18nKey="stats:labelAmountFromPeriodBeforeLastPeriod" values={{titleStat:titleStat, prevAmount: prevAmount, status: status}}/>,
                    label: t("stats:fromLastPeriod")
                };
            }
        }
    };

    const dataForLabels = makingLabel();
    return (
        <Card mb={3}>
            <ButtonBase onClick={showGraphic}>
                <CardContent>
                    <FlexRowSpaceBetween

                    >
                        <div>
                            <Typography variant="h6" mb={4} align={'left'} >
                                {title}
                            </Typography>
                            <Typography variant="h3" mb={3} align={'left'}>
                                {amount}
                            </Typography>
                        </div>
                    </FlexRowSpaceBetween>

                    <Grid container>
                        <Tooltip title={dataForLabels.tooltip}>
                            <Chip
                                label={dataForLabels.percentage}
                                style={{
                                    ...colorSettings,
                                    position: "unset",
                                    borderRadius: "2px",
                                    marginRight: "8px",

                                }}
                            />
                        </Tooltip>
                        <Typography variant="body2" style={{
                            fontSize: 14,
                            color: "#8a8a8a"
                        }}>{dataForLabels.label}</Typography>
                    </Grid>
                </CardContent>
            </ButtonBase>
        </Card>
    );
};

export default Stats;
